import type { NewWorkoutNote } from '@heraion/shared';

import { uuidv4 } from './uuid';
import { noteChanges } from './state/noteEventReducer';
import type { LoggerDraft } from './state/loggerDraftTypes';
import type { EditingTarget } from './LoggerScreen';
import type { LoggerHostDeps, LoggerWriter } from './hostDeps';

// Persist the draft's timeline note events (Solution C) once the workout is
// saved, once per save, never per tap. Out of useSaveWorkout (file cap).
//
// Each write goes to its own outbox keyed to the saved session id, then
// drained: online at once, offline on the reconnect drain like the workout.
// Best-effort: the workout is already persisted, so a note failure never
// fails the save. Card logger never sets noteEvents: no-op.
//
// A correction (#24) seeds the draft with the session's saved notes
// (`editing.notes`). Against those: a new note is created, one whose text
// changed is updated, one the athlete removed is deleted, the rest are left
// alone. Notes that never loaded (`notes` absent) update and delete nothing.
// `writer`: this save's, so the notes go to its account (#42).
export function useFlushNoteEvents({
  draft,
  editing,
  deps,
}: {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  deps: LoggerHostDeps;
}) {
  return async (
    sessionId: string,
    writer: LoggerWriter,
    asNew = false,
  ): Promise<string[]> => {
    const failed: string[] = [];
    const saveNote = writer.saveNoteEvent ?? deps.saveNoteEvent;
    const updateNote = writer.updateNoteEvent ?? deps.updateNoteEvent;
    const deleteNote = writer.deleteNoteEvent ?? deps.deleteNoteEvent;
    const saved = !asNew && editing?.mode !== 'repeat' ? (editing?.notes ?? []) : [];
    for (const change of noteChanges(saved, draft.noteEvents ?? [])) {
      // One note that throws must not reach the workout's catch (#418).
      // Its text goes back to the caller (#511): the athlete is told, and
      // can copy it. A failed delete has no text to show.
      try {
        if (change.kind === 'delete') {
          await deleteNote?.(change.id);
          continue;
        }
        const ev = change.event;
        if (change.kind === 'update') {
          await updateNote?.(ev.id, ev.text);
          continue;
        }
        const body: NewWorkoutNote = {
          // Saved as new (#38): the old session's notes went with it, and the
          // new session gets its own, so no id is reused.
          id: asNew ? uuidv4() : ev.id,
          sessionId,
          performedAt: ev.performedAt,
          text: ev.text,
          ...(ev.exerciseContextId
            ? { exerciseContextId: ev.exerciseContextId }
            : {}),
        };
        await saveNote(body);
      } catch (e) {
        // Best-effort, like the host contract: the next note still goes.
        console.warn("note flush failed", e);
        if (change.kind !== 'delete') failed.push(change.event.text);
      }
    }
    return failed;
  };
}
